import { cn } from "@/utils/cn";
import { Check, Copy, Eraser, ExternalLink, MessageSquarePlus, Trash2, X } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { citationsText, designName, originalLink, paperName } from "../cite";
import type { FastCell, FastColumn, FastRow } from "../types";
import { ScrollFade } from "./ScrollFade";

const valueText = (value: FastCell["value"]) => (typeof value === "boolean" ? (value ? "Yes" : "No") : String(value));

export const cellText = (cell: FastCell | undefined) =>
  cell == null ? "" : cell.parts.length > 1 ? cell.parts.map((p) => valueText(p.value)).join(" / ") : valueText(cell.value);

/** How the shown value relates to its highlighted text, for tooltips and the evidence status line. */
export const cellOrigin = (cell: FastCell) =>
  cell.derived
    ? `Derived: ${cell.note ? `${cell.note} (${cell.derived})` : cell.derived}`
    : cell.written
    ? "Written from the document's text, which alone does not answer this column"
    : cell.normalized && !cell.parts.length
      ? `Normalized from “${cell.text}”`
      : cell.normalized
        ? `Normalized from “${cell.parts.map((p) => p.text).join(" / ")}”`
        : undefined;

export type Span = { start: number; end: number; key: string; label: string; confidence: number };

/**
 * A passage with the exact words of each cell marked and the cell's label under them; the focused
 * cell's words stand out. The labels sit in the line gap, so the sentence reads as it is.
 */
export const SpanPassage = ({
  text,
  spans,
  focusKey,
  onSelect,
  labels = true,
  core,
}: {
  text: string;
  spans: Span[];
  focusKey: string | null;
  onSelect?: (key: string) => void;
  /** Each span's column label under it; off when the passage shows one value. */
  labels?: boolean;
  /** The quoted sentence; the text around it is context and fades away from it. */
  core?: Range;
}) => {
  const focusRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const mark = focusRef.current;
    const box = mark?.closest<HTMLElement>("[data-passages]");
    if (!mark || !box) return;
    // Scroll only the passages, not the page around them.
    const top = mark.getBoundingClientRect().top - box.getBoundingClientRect().top + box.scrollTop;
    box.scrollTo({ top: top - box.clientHeight / 2, behavior: "smooth" });
    const wide = mark.closest("[data-passage-table]")?.parentElement;
    if (wide) {
      const left = mark.getBoundingClientRect().left - wide.getBoundingClientRect().left + wide.scrollLeft;
      wide.scrollTo({ left: left - wide.clientWidth / 3, behavior: "smooth" });
    }
  }, [focusKey]);
  focusRef.current = null;
  const ordered = useMemo(() => {
    let end = 0;
    return [...spans]
      .filter((s) => focusKey == null || s.key === focusKey)
      .sort((a, b) => a.start - b.start)
      .filter((s) => s.start >= end && ((end = s.end), true));
  }, [spans, focusKey]);

  /** text[from, to) with the spans in it marked; a span cut by a table cell carries its label where it ends. */
  const marked = (from: number, to: number) => {
    const out: React.ReactNode[] = [];
    let at = from;
    ordered.forEach((span, i) => {
      const s = Math.max(span.start, from);
      const e = Math.min(span.end, to);
      if (e <= s) return;
      out.push(...plain(at, s));
      const focused = focusKey === span.key;
      out.push(
        <mark
          key={`${span.key}-${i}-${s}`}
          ref={focused ? (el) => { if (el && !focusRef.current) focusRef.current = el; } : undefined}
          title={`${span.label} · confidence ${Math.round(span.confidence * 100)}%`}
          data-span-key={span.key}
          data-focused={focused || undefined}
          onClick={onSelect ? () => onSelect(span.key) : undefined}
          className={cn(
            "rounded-[2px] px-0.5 text-zinc-900 shadow-[inset_0_-2px_0_var(--color-zinc-400)] transition-colors",
            onSelect && "cursor-pointer",
            focused ? "bg-zinc-300 ring-2 ring-zinc-800 ring-offset-1" : "bg-zinc-200/70",
          )}
        >
          {text.slice(s, e)}
        </mark>,
      );
      if (labels && span.end <= to) {
        out.push(
          <span
            key={`${span.key}-${i}-label`}
            data-span-label
            className="font-ui mx-[3px] whitespace-nowrap align-[1px] text-[8px] font-semibold uppercase tracking-wide text-zinc-500"
            aria-hidden
          >
            {span.label}
          </span>,
        );
      }
      at = e;
    });
    out.push(...plain(at, to));
    return out;
  };

  /** text[from, to) unmarked; context words fade from the quoted sentence out to the chunk's ends. */
  const plain = (from: number, to: number): React.ReactNode[] => {
    if (!core || (from >= core.from && to <= core.to)) return [text.slice(from, to)];
    const out: React.ReactNode[] = [];
    const words = /\S+\s*|\s+/g;
    words.lastIndex = from;
    for (let m = words.exec(text); m && m.index < to; m = words.exec(text)) {
      const a = m.index;
      const b = Math.min(a + m[0].length, to);
      const mid = (a + b) / 2;
      const away = mid < core.from ? (core.from - mid) / core.from : mid > core.to ? (mid - core.to) / Math.max(1, text.length - core.to) : 0;
      const word = text.slice(a, b);
      out.push(away > 0 ? <span key={a} style={{ opacity: 1 - 0.75 * Math.min(1, away) }}>{word}</span> : word);
    }
    return out;
  };

  return (
    <div className="space-y-2 text-[12.5px] leading-relaxed text-zinc-700">
      {blocks(text).map((block) =>
        block.kind === "text" ? (
          <p key={block.from} className={cn("whitespace-pre-wrap", block.heading && "font-semibold text-zinc-900")}>
            {marked(block.from, block.to)}
          </p>
        ) : (
          <ScrollFade key={block.from}>
            <table data-passage-table className="border-collapse text-[11.5px] leading-snug">
              <tbody>
                {block.rows.map((row, r) => (
                  <tr key={r} className={cn("border-b border-zinc-200 align-top", r === 0 && block.header && "border-zinc-300")}>
                    {row.map((cell, c) => {
                      const Cell = r === 0 && block.header ? "th" : "td";
                      return (
                        <Cell
                          key={c}
                          className={cn(
                            "min-w-[48px] max-w-[260px] px-2 py-1 text-left first:pl-0",
                            Cell === "th" ? "font-ui font-medium text-zinc-500" : "tabular-nums",
                          )}
                        >
                          {marked(cell.from, cell.to)}
                        </Cell>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollFade>
        ),
      )}
    </div>
  );
};

type Range = { from: number; to: number };
type Block =
  | (Range & { kind: "text"; heading: boolean })
  | (Range & { kind: "table"; header: boolean; rows: Range[][] });

const TABLE_LINE = /^\s*\|.*\|\s*$/;
const RULE_LINE = /^\s*\|(\s*:?-{2,}:?\s*\|)+\s*$/;

/** Splits a passage into prose and markdown tables, keeping offsets into the passage so spans still land. */
const blocks = (text: string): Block[] => {
  const lines: Range[] = [];
  for (let at = 0; at <= text.length; ) {
    const nl = text.indexOf("\n", at);
    const to = nl < 0 ? text.length : nl;
    lines.push({ from: at, to });
    at = to + 1;
  }
  const out: Block[] = [];
  let prose: Range | null = null;
  const flush = () => {
    if (prose && prose.to > prose.from) out.push({ kind: "text", heading: false, ...prose });
    prose = null;
  };
  for (let i = 0; i < lines.length; ) {
    const line = text.slice(lines[i].from, lines[i].to);
    if (TABLE_LINE.test(line) && i + 1 < lines.length && TABLE_LINE.test(text.slice(lines[i + 1].from, lines[i + 1].to))) {
      flush();
      const rows: Range[][] = [];
      let header = false;
      const from = lines[i].from;
      let to = from;
      for (; i < lines.length && TABLE_LINE.test(text.slice(lines[i].from, lines[i].to)); i++) {
        to = lines[i].to;
        if (RULE_LINE.test(text.slice(lines[i].from, lines[i].to))) {
          header = rows.length === 1;
          continue;
        }
        rows.push(cells(text, lines[i]));
      }
      out.push({ kind: "table", header, rows, from, to });
      continue;
    }
    const heading = /^#{1,6}\s/.exec(line);
    if (heading) {
      flush();
      out.push({ kind: "text", heading: true, from: lines[i].from + heading[0].length, to: lines[i].to });
    } else {
      prose = prose ? { from: prose.from, to: lines[i].to } : { ...lines[i] };
    }
    i++;
  }
  flush();
  return out;
};

const cells = (text: string, line: Range): Range[] => {
  const pipes: number[] = [];
  for (let k = line.from; k < line.to; k++) if (text[k] === "|") pipes.push(k);
  const out: Range[] = [];
  for (let k = 0; k + 1 < pipes.length; k++) {
    let from = pipes[k] + 1;
    let to = pipes[k + 1];
    while (from < to && text[from] === " ") from++;
    while (to > from && text[to - 1] === " ") to--;
    out.push({ from, to });
  }
  return out;
};

type Chunk = { text: string; spans: Span[]; label: string; core?: Range };

const SENTENCE_END = /[.!?](?=\s|$)/g;
const MAX_BEFORE = 260;
const MAX_AFTER = 320;

/** Characters of the sentence before and after the quoted one shown around it, by default. */
const CONTEXT = { before: 240, after: 240 };
/** Around a value clamped to three lines: little before it, so the quote stays in view. */
const CONTEXT_CLAMPED = { before: 80, after: 240 };

/**
 * The sentence of a passage that holds text[start, end) (a table row under its header for tables), with the span's
 * offsets moved into it, and up to a sentence on either side of it (`core` is the quoted sentence within the text).
 * Long sentences are cut at word boundaries.
 */
export const chunkOf = (
  text: string,
  start: number,
  end: number,
  context: { before: number; after: number } = CONTEXT,
): { text: string; start: number; end: number; core?: Range } => {
  const lineStart = text.lastIndexOf("\n", Math.max(0, start - 1)) + 1;
  const nl = text.indexOf("\n", end);
  const lineEnd = nl < 0 ? text.length : nl;
  const line = text.slice(lineStart, lineEnd);
  if (TABLE_LINE.test(line) && !line.includes("\n")) {
    let head = lineStart;
    while (head > 0) {
      const prev = text.lastIndexOf("\n", head - 2) + 1;
      if (!TABLE_LINE.test(text.slice(prev, head - 1))) break;
      head = prev;
    }
    if (head === lineStart) return { text: line, start: start - lineStart, end: end - lineStart };
    const headerEnd = text.indexOf("\n", head);
    const header = text.slice(head, headerEnd);
    const ruleEnd = text.indexOf("\n", headerEnd + 1);
    const rule = text.slice(headerEnd + 1, ruleEnd < 0 ? text.length : ruleEnd);
    const top = RULE_LINE.test(rule) ? `${header}\n${rule}\n` : `${header}\n`;
    if (head + top.length >= lineStart) return { text: text.slice(head, lineEnd), start: start - head, end: end - head };
    return { text: top + line, start: start - lineStart + top.length, end: end - lineStart + top.length };
  }
  let from = lineStart;
  for (const m of text.slice(lineStart, start).matchAll(SENTENCE_END)) from = lineStart + m.index + 1;
  let to = lineEnd;
  SENTENCE_END.lastIndex = 0;
  const after = SENTENCE_END.exec(text.slice(end, lineEnd));
  if (after) to = end + after.index + 1;
  let cutFront = false;
  let cutBack = false;
  if (start - from > MAX_BEFORE) {
    from = text.indexOf(" ", start - MAX_BEFORE) + 1 || start - MAX_BEFORE;
    cutFront = true;
  }
  if (to - end > MAX_AFTER) {
    to = text.lastIndexOf(" ", end + MAX_AFTER);
    if (to <= end) to = end + MAX_AFTER;
    cutBack = true;
  }
  while (from < start && /\s/.test(text[from])) from++;
  let ctxFrom = from;
  let ctxTo = to;
  if (!cutFront && from > lineStart && context.before > 0) {
    let prevEnd = from;
    while (prevEnd > lineStart && /\s/.test(text[prevEnd - 1])) prevEnd--;
    ctxFrom = lineStart;
    SENTENCE_END.lastIndex = 0;
    for (const m of text.slice(lineStart, prevEnd - 1).matchAll(SENTENCE_END)) ctxFrom = lineStart + m.index + 1;
    if (from - ctxFrom > context.before) ctxFrom = text.indexOf(" ", from - context.before) + 1 || from - context.before;
    while (ctxFrom < from && /\s/.test(text[ctxFrom])) ctxFrom++;
  }
  if (!cutBack && to < lineEnd && context.after > 0) {
    SENTENCE_END.lastIndex = 0;
    const next = SENTENCE_END.exec(text.slice(to, lineEnd));
    ctxTo = next ? to + next.index + 1 : lineEnd;
    if (ctxTo - to > context.after) {
      const cut = text.lastIndexOf(" ", to + context.after);
      ctxTo = cut > to ? cut : to + context.after;
    }
  }
  const prefix = cutFront ? "…" : "";
  const body = text.slice(ctxFrom, ctxTo).trimEnd();
  return {
    text: prefix + body + (cutBack ? "…" : ""),
    start: start - ctxFrom + prefix.length,
    end: end - ctxFrom + prefix.length,
    core: { from: from - ctxFrom + prefix.length, to: Math.min(to - ctxFrom, body.length) + prefix.length },
  };
};

/** The chunks a cell's value comes from: one per quoted part, or per input of a calculated value. */
const chunksOf = (cell: FastCell, column: FastColumn, context?: { before: number; after: number }): Chunk[] => {
  const locs = cell.derived && cell.sources?.length
    ? cell.sources.map((p) => ({ p, confidence: cell.confidence }))
    : (cell.parts.length ? cell.parts : [cell]).map((part) => ({ p: part.provenance, confidence: part.confidence }));
  const out: Chunk[] = [];
  for (const { p, confidence } of locs) {
    if (!p?.passage_text || p.end <= p.start) continue;
    const c = chunkOf(p.passage_text, p.start, p.end, context);
    const span = { start: c.start, end: c.end, key: column.key, label: column.label, confidence };
    const same = out.find((o) => o.text === c.text);
    if (same) same.spans.push(span);
    else out.push({ text: c.text, spans: [span], label: p.passage_label, core: c.core });
  }
  return out;
};

/** A value filed under one of the column's labels: its derivation is `"source words" → label`, not arithmetic. */
const isLabeled = (cell: FastCell) => !!cell.normalized && !!cell.derived && /^".*" → /s.test(cell.derived);

/** The model's reason for a label, without the generic "The paper says …" fallback the builder writes. */
const labelReason = (cell: FastCell) => (cell.note && !cell.note.startsWith("The paper says") ? cell.note.replace(/\.+$/, "") : null);

/** How the value follows from its source, in a few words. */
const howFound = (cell: FastCell) =>
  isLabeled(cell)
    ? "Labeled"
    : cell.derived
      ? "Calculated"
      : cell.written
        ? "Summarized"
        : cell.normalized
          ? "Converted"
          : "Quoted";

const HOW_TITLE: Record<string, string> = {
  Quoted: "The source's exact words",
  Converted: "The source's words, in the column's units or format",
  Labeled: "The source's words, filed under one of the column's labels",
  Summarized: "Written from the highlighted text, which alone does not answer the column",
  Calculated: "Worked out from numbers the source states",
};

/** What the source itself says, when the value shown is not its exact words. */
const sourceWords = (cell: FastCell) =>
  cell.original ?? (cell.parts.length ? cell.parts.map((p) => p.text).join(" / ") : cell.text);

function SectionHeading({ children, aside }: { children: ReactNode; aside?: ReactNode }) {
  return (
    <div className="mb-2 flex items-baseline justify-between gap-3 border-b border-zinc-200 pb-1.5">
      <h3 className="font-ui text-[13px] font-semibold text-zinc-900">{children}</h3>
      {aside}
    </div>
  );
}

type Props = {
  row: FastRow;
  columns: FastColumn[];
  focusKey: string | null;
  onFocus: (key: string | null) => void;
  onClose: () => void;
  /** Absent when the dataset can't be edited (shared, or being built). */
  onDeleteRow?: () => void;
  onClearCell?: (key: string) => void;
  /** Attach a cell to the next chat message. */
  onAddToChat?: (key: string) => void;
};

/** Under its row: the source the row comes from, then each value with the sentence it was taken from. */
export const EvidencePanel = ({ row, columns, focusKey, onFocus, onClose, onDeleteRow, onClearCell, onAddToChat }: Props) => {
  const source = row.source;
  const design = designName(source.design);
  const cites = citationsText(source.citations);
  const link = originalLink(source);
  const references = [source.citation, ...(source.data_citations ?? [])].filter((c): c is string => !!c);
  const found = columns.filter((c) => row.cells[c.key]);
  const missing = columns.filter((c) => !row.cells[c.key] && c.value_type !== "image");
  const focused = found.find((c) => c.key === focusKey);
  const shown = focused ? [focused] : found;

  return (
    <section className="rounded-md border border-zinc-200 bg-white shadow-sm" aria-label="Row evidence">
      <header className="flex items-start justify-between gap-3 border-b border-zinc-100 px-4 py-3">
        <div className="min-w-0">
          <h3 className="font-ui text-[13px] font-semibold text-zinc-900">Source</h3>
          <h2 className="mt-1 line-clamp-2 text-[14px] leading-snug text-zinc-900" title={source.title}>
            <span className="font-semibold">{paperName(row)}</span>
            {source.title && paperName(row) !== source.title && <span className="text-zinc-600"> · {source.title}</span>}
          </h2>
          <p className="font-ui mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs text-zinc-500">
            {design && <span className="rounded-[2px] bg-zinc-100 px-1 font-medium text-zinc-700">{design}</span>}
            {source.journal && <span className="italic">{source.journal}</span>}
            {source.year && <span>{source.year}</span>}
            {cites && <span className="tabular-nums">{cites}</span>}
            {link && (
              <a
                className="inline-flex items-center gap-1 font-medium text-zinc-800 underline decoration-zinc-300 underline-offset-2 hover:decoration-zinc-800"
                href={link.href}
                target="_blank"
                rel="noreferrer"
              >
                View on {link.label} <ExternalLink className="h-3 w-3" aria-hidden />
              </a>
            )}
          </p>
          {references.length > 0 && <References items={references} />}
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {onDeleteRow && (
            <button type="button" onClick={onDeleteRow} className="font-ui inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-red-700">
              <Trash2 className="h-3.5 w-3.5" aria-hidden /> Delete row
            </button>
          )}
          <button type="button" onClick={onClose} className="text-zinc-400 hover:text-zinc-800" aria-label="Close evidence">
            <X className="h-4 w-4" />
          </button>
        </div>
      </header>

      <div data-passages className="max-h-[24rem] overflow-y-auto overscroll-y-contain px-4 py-3">
        <SectionHeading
          aside={
            focusKey && (
              <button type="button" onClick={() => onFocus(null)} className="font-ui text-xs text-zinc-500 hover:text-zinc-900">
                Show the whole row
              </button>
            )
          }
        >
          {focusKey ? "Where this value comes from" : "What was taken from it"}
        </SectionHeading>
        <ul className="divide-y divide-zinc-100">
          {shown.map((column) => (
            <ValueItem
              key={column.key}
              column={column}
              cell={row.cells[column.key]}
              open={!!focused}
              onSelect={() => onFocus(focused ? null : column.key)}
              onAddToChat={focused && onAddToChat ? () => onAddToChat(column.key) : undefined}
              onClearCell={focused && onClearCell ? () => onClearCell(column.key) : undefined}
            />
          ))}
        </ul>
        {!focused && missing.length > 0 && (
          <p className="font-ui mt-3 text-xs text-zinc-400">Not found in this source: {missing.map((c) => c.label).join(", ")}</p>
        )}
        {focusKey && !focused && (
          <p className="font-ui text-xs text-zinc-500">
            {columns.find((c) => c.key === focusKey)?.label ?? "This column"} was not found in this source.
          </p>
        )}
      </div>
    </section>
  );
};

/** One value: what it is, how it follows from the source, and the sentence it comes from. */
function ValueItem({
  column,
  cell,
  open,
  onSelect,
  onAddToChat,
  onClearCell,
}: {
  column: FastColumn;
  cell: FastCell;
  open: boolean;
  onSelect: () => void;
  onAddToChat?: () => void;
  onClearCell?: () => void;
}) {
  const how = howFound(cell);
  const chunks = chunksOf(cell, column, open ? undefined : CONTEXT_CLAMPED);
  const reason = isLabeled(cell) ? labelReason(cell) : null;
  return (
    <li className="grid grid-cols-[minmax(7rem,11rem)_1fr] gap-x-4 py-2.5 first:pt-1">
      <div className="font-ui pt-px text-xs leading-snug text-zinc-500">
        {open ? (
          column.label
        ) : (
          <button type="button" onClick={onSelect} className="text-left hover:text-zinc-900 hover:underline">
            {column.label}
          </button>
        )}
      </div>
      <div className="min-w-0">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className={cn("font-semibold text-zinc-900", open ? "text-[15px]" : "text-[13px]")}>{cellText(cell)}</span>
        <span className="font-ui rounded-[2px] bg-zinc-100 px-1 text-2xs font-medium text-zinc-600" title={HOW_TITLE[how]}>
          {how}
        </span>
        {cell.confidence < 0.6 && <span className="font-ui text-2xs text-amber-700">low confidence</span>}
        {open && onAddToChat && (
          <button type="button" onClick={onAddToChat} className="font-ui ml-auto inline-flex items-center gap-1 text-xs text-zinc-600 hover:text-zinc-950">
            <MessageSquarePlus className="h-3 w-3" aria-hidden /> Add to chat
          </button>
        )}
        {open && onClearCell && (
          <button type="button" onClick={onClearCell} className={cn("font-ui inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-red-700", !onAddToChat && "ml-auto")}>
            <Eraser className="h-3 w-3" aria-hidden /> Clear value
          </button>
        )}
      </div>
      {cell.derived && !isLabeled(cell) && (
        <p className="mt-1 text-xs leading-snug text-zinc-600">
          {cell.note && <span>{cell.note.replace(/\.+$/, "")}. </span>}
          <span className="font-mono text-[11px] text-zinc-800">{cell.derived}</span>
        </p>
      )}
      {cell.normalized && (!cell.derived || isLabeled(cell)) && (
        <p className="mt-1 text-xs text-zinc-500">
          The source says “{sourceWords(cell)}”.{reason && ` ${reason}.`}
        </p>
      )}
      {chunks.map((chunk, i) => (
        <figure key={i} className={cn("mt-1.5 border-l-2 border-zinc-200 pl-2.5", !open && "line-clamp-3")}>
          <SpanPassage text={chunk.text} spans={chunk.spans} focusKey={open ? column.key : null} labels={false} core={chunk.core} />
          {chunk.label && (open || chunks.length > 1) && (
            <figcaption className="font-ui mt-0.5 text-2xs text-zinc-400">{chunk.label}</figcaption>
          )}
        </figure>
      ))}
      </div>
    </li>
  );
}

/** The record's formal references, each with a copy button. */
function References({ items }: { items: string[] }) {
  const [copied, setCopied] = useState<number | null>(null);
  return (
    <ul className="font-ui mt-1 space-y-0.5 text-xs text-zinc-600" aria-label="References">
      {items.map((text, i) => (
        <li key={text} className="flex items-start gap-1.5">
          <span className="min-w-0 break-words">{text}</span>
          <button
            type="button"
            className="shrink-0 text-zinc-400 hover:text-zinc-800"
            aria-label="Copy reference"
            onClick={() => {
              void navigator.clipboard.writeText(text).then(() => setCopied(i));
            }}
          >
            {copied === i ? <Check className="h-3 w-3" aria-hidden /> : <Copy className="h-3 w-3" aria-hidden />}
          </button>
        </li>
      ))}
    </ul>
  );
}
